export type PeeqdContentTabId = "stories" | "posts" | "reels" | "highlights" | "reposts";

export interface PeeqdContentTab {
  id: PeeqdContentTabId;
  label: string;
}

/** Content-type tabs inside the preview mockup */
export const PEEQD_CONTENT_TABS: PeeqdContentTab[] = [
  { id: "stories", label: "Stories" },
  { id: "posts", label: "Posts" },
  // { id: "reels", label: "Reels" },
  // { id: "highlights", label: "Highlights" },
  // { id: "reposts", label: "Reposts" },
];

export const PEEQD_SECTION_DEFAULT_CONTENT_TAB: Partial<Record<PeeqdReportTabId, PeeqdContentTabId>> = {
  stories: "stories",
  content: "posts",
};

export type PeeqdReportTabId = "stories" | "comments" | "content" | "likes" | "unfollows";

export interface PeeqdReportTab {
  id: PeeqdReportTabId;
  label: string;
  shortLabel: string;
}

/** Report categories - shared across all landing pages */
export const PEEQD_REPORT_TABS: PeeqdReportTab[] = [
  { id: "unfollows", label: "Followers ", shortLabel: "Followers" },
  { id: "likes", label: "Likes", shortLabel: "Likes" },
  { id: "stories", label: "Stories", shortLabel: "Stories" },
  { id: "comments", label: "Comments", shortLabel: "Comments" },
  { id: "content", label: "Posts", shortLabel: "Posts" },
];

/** Dashboard sections that render a coming-soon placeholder instead of live data. */
export const PEEQD_COMING_SOON_SECTIONS: Partial<Record<PeeqdReportTabId, string>> = {
  stories: "Story alerts and archives are on the way.",
  comments: "Comment tracking and alerts are on the way.",
  content: "Post monitoring and exports are on the way.",
};

export function isComingSoonSection(section: PeeqdReportTabId): boolean {
  return section in PEEQD_COMING_SOON_SECTIONS;
}

/** Maps each tool landing page to its default report tab */
export const PEEQD_TOOL_REPORT_TAB: Record<PeeqdToolId, PeeqdReportTabId> = {
  "followers-following": "unfollows",
  "story-alerts": "stories",
  "competitor-watch": "unfollows",
  "audience-audit": "likes",
};

export function getDefaultReportTab(toolId?: PeeqdToolId): PeeqdReportTabId {
  if (!toolId) return "unfollows";
  return PEEQD_TOOL_REPORT_TAB[toolId];
}

export type PeeqdToolId =
  | "followers-following"
  | "story-alerts"
  | "competitor-watch"
  | "audience-audit";

export interface PeeqdToolContent {
  id: PeeqdToolId;
  eyebrow: string;
  crumb: string;
  title: string;
  titleHtml: string;
  description: string;
  cta: string;
  isTracker: boolean;
}

export interface PeeqdFeatureBlock {
  label: string;
  title: string;
  description: string;
  bullets?: string[];
  visual:
    | "changelog"
    | "story-alert"
    | "competitor-stats"
    | "audit-chart"
    | "likes-analytics"
    | "activity-report";
  reversed?: boolean;
}

export interface PeeqdHowItWorksContent {
  title: string;
  titleHtml: string;
  description: string;
  steps: { n: string; title: string; desc: string }[];
}

export interface PeeqdHomeFactsStep {
  title: string;
  bodyHtml: string;
}

export interface PeeqdHomeFactsFootnote {
  label: string;
  body: string;
}

export interface PeeqdHomeFactsContent {
  titleHtml: string;
  intro: string;
  steps: PeeqdHomeFactsStep[];
  footnotes: PeeqdHomeFactsFootnote[];
}

export interface PeeqdFaqItem {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}

export interface PeeqdHomeNavItem {
  label: string;
  href: string;
}

export interface PeeqdHomeWhoItem {
  id: "watch" | "question" | "growth" | "competitors";
  title: string;
  description: string;
}

/** Homepage section anchors - add entries here as new sections ship. */
export const PEEQD_HOME_NAV: PeeqdHomeNavItem[] = [
  { label: "Example", href: "/#preview" },
  { label: "Features", href: "/#features" },
  { label: "Use cases", href: "/#who" },
  { label: "How it works", href: "/#facts" },
  { label: "FAQ", href: "/#faq" },
];

export const PEEQD_HOME_WHO = {
  titleHtml: 'One tracker. <span class="grad-text">Every reason.</span>',
  items: [
    {
      id: "watch" as const,
      title: "Keep up with accounts you care about",
      description:
        "A favorite artist, a public figure you cover, a brand you resell, that account everyone's talking about. Their follows, unfollows and posts - logged for you, so you never have to refresh or scroll again.",
    },
    {
      id: "question" as const,
      title: "Answer a specific question",
      description:
        "Who did they just follow? Did that account unfollow this one? Are two accounts newly mutual? One-off questions deserve one clean answer, not an evening of scrolling shuffled lists. Add the account, let Peeqd watch, get the answer.",
    },
    {
      id: "growth" as const,
      title: "Understand your own growth",
      description:
        "See exactly when followers arrive and leave, and which post caused it. Know your real churn - not just the net number Instagram shows you. Native Instagram Insights only covers your own business account, and only partially.",
    },
    {
      id: "competitors" as const,
      title: "Watch competitors",
      description:
        "Track rivals' follower velocity, posting cadence, engagement and who they've started following. Spot the campaign that's working for them before your next planning meeting.",
    },
  ] satisfies PeeqdHomeWhoItem[],
};

export const PEEQD_HOME_CONTENT: PeeqdToolContent = {
  id: "followers-following",
  eyebrow: "Follower tracker · Likes viewer · Activity reports",
  crumb: "Home",
  title: "Instagram Activity Tracker for any account.",
  titleHtml:
    'Instagram <span class="grad-text">Activity</span> Tracker',
  description:
    "See anyone's recent followers, unfollowers, likes, and how their posts perform - then get it all summarized in a clean activity report. No manual checking, no list-scrolling, no guesswork.",
  cta: "Search now",
  isTracker: true,
};

/** Homepage SEO — title leads with H1 keyword; description tuned for SERP intent. */
export const PEEQD_HOME_SEO = {
  title: "Instagram Activity Tracker - Followers, Likes & Reports",
  description:
    "See anyone's recent followers, unfollowers, likes, and how their posts perform. Peeqd tracks any public Instagram account and summarizes changes in a clean activity report.",
  keywords: [
    "instagram activity tracker",
    "instagram follower tracker",
    "instagram unfollow tracker",
    "see who someone follows on instagram",
    "instagram likes viewer",
    "instagram activity report",
    "track instagram followers",
    "instagram following tracker",
  ],
} as const;

export const PEEQD_TOOLS_CONTENT: Record<PeeqdToolId, PeeqdToolContent> = {
  "followers-following": {
    id: "followers-following",
    eyebrow: "Followers & Following",
    crumb: "Followers & Following",
    title: "Their followers and following - dated, and diffed.",
    titleHtml:
      'Instagram Follower Tracker: See Their Recent <span class="grad">Followers</span> and <span class="grad">Following</span>',
    description:
      "One tracker for both sides of any public account. See who followed, who unfollowed, and who they just started following - every change tracked from the moment you start.",
    cta: "Analyze",
    isTracker: true,
  },
  "story-alerts": {
    id: "story-alerts",
    eyebrow: "Story Alerts",
    crumb: "Story Alerts",
    title: "Get pinged the second they post a story.",
    titleHtml:
      'Get pinged the second<br><span class="grad">they post a story.</span>',
    description:
      "Instagram won't alert you for accounts you don't follow. Peeqd will - the moment a story goes live, in time to view it anonymously before it expires. You never enter their viewer list, and we store the event, not the media.",
    cta: "Turn on alerts",
    isTracker: false,
  },
  "competitor-watch": {
    id: "competitor-watch",
    eyebrow: "Competitor Watch",
    crumb: "Competitor Watch",
    title: "One daily digest for every account that matters.",
    titleHtml:
      'One daily digest for<br><span class="grad">every account that matters.</span>',
    description:
      "Track up to 40 competitors and creators and get their follows, unfollows, follower swing and story cadence in a single morning summary - without following a single one of them.",
    cta: "Build my watchlist",
    isTracker: false,
  },
  "audience-audit": {
    id: "audience-audit",
    eyebrow: "Audience Audit",
    crumb: "Audience Audit",
    title: "Know what a following is actually worth.",
    titleHtml:
      'Know what a following<br><span class="grad">is actually worth.</span>',
    description:
      "Break any public account down by real vs. suspicious followers, engagement rate and growth trend - the numbers behind the vanity count, before you spend on a partnership.",
    cta: "Run an audit",
    isTracker: false,
  },
};

export const PEEQD_HOME_FEATURES: PeeqdFeatureBlock[] = [
  {
    label: "Follower tracker",
    title: "Instagram Follower Tracker",
    description:
      "See anyone's new followers and unfollows, plus who they started and stopped following - automatically, instead of comparing lists by hand. Works as a follower tracker and an unfollower tracker in one.",
    visual: "changelog",
  },
  {
    label: "Posts & likes",
    title: "Posts & Likes Analytics",
    description:
      "See the posts an account liked - and how their own posts perform: likes, comments, engagement rate, what's working.",
    visual: "likes-analytics",
    reversed: true,
  },
  {
    label: "AI reports",
    title: "AI Activity Reports",
    description:
      "Tracking is step one - the report is the payoff. Peeqd rolls everything into a plain-English summary: growth, churn, top posts, interests and engagement patterns, readable in two minutes.",
    visual: "activity-report",
  },
];

export const PEEQD_TOOL_FEATURES: Record<PeeqdToolId, PeeqdFeatureBlock> = {
  "followers-following": {
    label: "The timeline",
    title: "Recent follows, actually dated.",
    description:
      'Instagram scrambles the following list and strips every timestamp. Peeqd rebuilds the timeline by diffing snapshots - so "recently followed" means 2:14pm today, not the top of a shuffled list. Honest by construction.',
    bullets: [
      "Every new follow & unfollow, to the hour",
      "Both followers and following in one tracker",
      "Export the raw timeline to CSV anytime",
    ],
    visual: "changelog",
  },
  "story-alerts": {
    label: "Story alerts",
    title: "Know the moment they post.",
    description:
      "Instagram won't alert you for accounts you don't follow. Peeqd pings you the second a story goes live on any public account - in time to view it anonymously before it expires. You never appear in their viewer list.",
    bullets: [
      "Push notification in under 60 seconds",
      "Anonymous viewing - you're never on their list",
      "We store the event, never the story media",
    ],
    visual: "story-alert",
  },
  "competitor-watch": {
    label: "Competitor watch",
    title: "Watch the accounts that matter.",
    description:
      "Track up to 40 competitors and creators and get one clean digest a day: who they followed, who unfollowed them, follower swing and story cadence. The intel marketers actually use.",
    bullets: [
      "One morning email for your whole watchlist",
      "Follows, unfollows and story cadence per account",
      "No following required - completely invisible",
    ],
    visual: "competitor-stats",
  },
  "audience-audit": {
    label: "Audience audit",
    title: "Know what a following is actually worth.",
    description:
      "Break any public account down by real vs. suspicious followers, engagement rate and growth trend - the numbers behind the vanity count, before you spend on a partnership.",
    bullets: [
      "Real vs. bot follower split",
      "Engagement rate benchmarked to niche",
      "Growth trend over the last 90 days",
    ],
    visual: "audit-chart",
  },
};

export const PEEQD_HOME_FACTS: PeeqdHomeFactsContent = {
  titleHtml:
    'How Peeqd actually <span class="grad-text">tracks an account.</span>',
  intro:
    "Most trackers keep their method vague. Ours is the product - here's exactly what happens after you add a username, and why it's built this way.",
  steps: [
    {
      title: "Full snapshot - the entire follower and following lists, captured.",
      bodyHtml:
        "Not the top of the list, all of it. Instagram sorts these lists algorithmically and the order can reshuffle between visits - so tools that read only the visible top both miss changes and invent them. A complete snapshot is the only trustworthy starting point.",
    },
    {
      title: "Snapshot again, compare - every change surfaces.",
      bodyHtml:
        "Instagram attaches no dates to follows, so the only verifiable way to know what changed is to capture the list again and diff it. This also catches what the profile itself hides: above 10,000 followers Instagram rounds the public count, so an account can gain and lose hundreds while its number never moves. The diff sees every single one.",
    },
    {
      title: "Posts and likes pass - daily.",
      bodyHtml:
        "Recent posts are checked every day: new posts, likes and comments on eligible posts, engagement building over time. Combined with the follower diff, you see not just <em>that</em> the account changed, but what caused it.",
    },
    {
      title: "The report writes itself.",
      bodyHtml:
        "Raw changes roll up into an AI activity summary: growth, churn, top posts, engagement patterns, notable follows. The full log stays available - the report just means you don't have to read it.",
    },
  ],
  footnotes: [
    {
      label: "Why tracking starts at zero:",
      body:
        "because Instagram publishes no follow dates, no tool can honestly show you dated history from before it started watching. Tools that claim to are reconstructing a shuffled list. Peeqd records from the moment you add an account - everything after that is real.",
    },
    {
      label: "Why serious tracking isn't free:",
      body:
        "every full snapshot of a large account costs real compute and data, which is why budget tools quietly batch weekly or track only a slice of the list. Peeqd does the complete job - that's what you're paying for.",
    },
  ],
};

export const PEEQD_HOME_HOW_IT_WORKS: PeeqdHowItWorksContent = {
  title: "The clock starts at t = 0.",
  titleHtml: 'The clock starts at <span class="grad">t = 0</span>.',
  description:
    "No backfilled history, no invented recent. We start recording the moment you add an account and build a real timeline from there.",
  steps: [
    {
      n: "01",
      title: "Add an account",
      desc: "Paste any public @username. No login, no following, nothing that touches their account.",
    },
    {
      n: "02",
      title: "We snapshot on a schedule",
      desc: "Hourly on Pro. Each snapshot is diffed against the last to catch every change with a real time.",
    },
    {
      n: "03",
      title: "You get dated changes",
      desc: "Follows, unfollows and story posts land in your feed with timestamps - and a push when it matters.",
    },
  ],
};

export const PEEQD_TOOL_HOW_IT_WORKS: Record<PeeqdToolId, PeeqdHowItWorksContent> = {
  "followers-following": {
    title: "Start tracking now.",
    titleHtml: 'Start tracking <span class="grad">now</span>.',
    description:
      "Paste a username and we start diffing followers and following from that moment - every change gets a real timestamp.",
    steps: [
      {
        n: "01",
        title: "Paste a @username",
        desc: "Any public Instagram account. We read both followers and following lists.",
      },
      {
        n: "02",
        title: "We snapshot & diff",
        desc: "Hourly on Pro. Each snapshot is compared to the last to catch every follow and unfollow.",
      },
      {
        n: "03",
        title: "See dated changes",
        desc: "Who followed, who unfollowed, who they started following - all with real timestamps.",
      },
    ],
  },
  "story-alerts": {
    title: "Never miss a story again.",
    titleHtml: 'Never miss a <span class="grad">story</span> again.',
    description:
      "Add any public account and get a push the second they post - even if you don't follow them.",
    steps: [
      {
        n: "01",
        title: "Add accounts to watch",
        desc: "Any public @username - competitors, exes, creators you don't want to follow publicly.",
      },
      {
        n: "02",
        title: "We monitor story rings",
        desc: "Peeqd checks for new stories on a schedule and detects the moment one goes live.",
      },
      {
        n: "03",
        title: "Get pinged instantly",
        desc: "Push notification in under 60 seconds. View anonymously before the 24-hour window closes.",
      },
    ],
  },
  "competitor-watch": {
    title: "Your morning intel, automated.",
    titleHtml: 'Your morning intel, <span class="grad">automated</span>.',
    description:
      "Build a watchlist of up to 40 accounts and get one digest every morning with everything that moved.",
    steps: [
      {
        n: "01",
        title: "Build your watchlist",
        desc: "Add competitors, creators and brands you care about - up to 40 on Agency.",
      },
      {
        n: "02",
        title: "We track everything",
        desc: "Follows, unfollows, follower count swings and story cadence for every account on your list.",
      },
      {
        n: "03",
        title: "One digest, every morning",
        desc: "A single email with what moved across your whole watchlist. No checking 40 profiles manually.",
      },
    ],
  },
  "audience-audit": {
    title: "Numbers behind the vanity count.",
    titleHtml: 'Numbers behind the <span class="grad">vanity count</span>.',
    description:
      "Run an audit on any public account before you spend on a partnership or sponsorship.",
    steps: [
      {
        n: "01",
        title: "Enter a @username",
        desc: "Any public account - influencer, brand, or potential partner.",
      },
      {
        n: "02",
        title: "We analyze the audience",
        desc: "Real vs. suspicious followers, engagement rate, and growth trend over 90 days.",
      },
      {
        n: "03",
        title: "Get the verdict",
        desc: "A clear breakdown of audience quality so you know what you're actually buying into.",
      },
    ],
  },
};

export const PEEQD_FAQ: PeeqdFaqItem[] = [
  {
    question:
      "Can you show me who they followed last month, before I signed up?",
    answer:
      "No - and nobody honestly can. Instagram doesn't reveal when a follow happened, so any tool showing you dated history from before you signed up is guessing at the order of a shuffled list. Peeqd starts tracking the moment you add an account and records every change from there.",
    defaultOpen: true,
  },
  {
    question: "Do I need access to the account I'm tracking?",
    answer:
      "No. Peeqd works with any public account and only reads what's publicly visible. Tracking is read-only - nothing is posted, liked or followed on anyone's behalf, and no interaction with the tracked profile takes place.",
    defaultOpen: true,
  },
  {
    question: "Can't I just check their following list myself?",
    answer:
      "You can look, but you can't reliably compare. Instagram sorts follower and following lists algorithmically - not by date - and the order can change between visits. Manual tracking means exporting thousands of names into a spreadsheet twice and diffing them. Peeqd automates exactly that, on a schedule.",
    defaultOpen: true,
  },
  {
    question: "Do you store their photos or content?",
    answer:
      "No. Peeqd records that activity happened - a post was published, it received likes - not the media itself. We don't archive or redistribute anyone's content; you view it on Instagram.",
    defaultOpen: true,
  },
  {
    question: "What about private accounts?",
    answer:
      "Private accounts can't be tracked, full stop. If an account is private, its owner chose that - Peeqd only works with information the account owner has made public.",
    defaultOpen: true,
  },
  {
    question: "Is there a free tracking tier?",
    answer:
      "There's a free lookup so you can see what a report looks like for any public account. Continuous tracking - the log, alerts and reports - is part of Pro, because keeping fresh data on an account costs real money every single day.",
  }
];

export const PEEQD_TOOL_FAQ: Record<PeeqdToolId, PeeqdFaqItem[]> = {
  "followers-following": PEEQD_FAQ,
  "story-alerts": [
    {
      question: "Can I get alerts for accounts I don't follow?",
      answer:
        "Yes - that's the whole point. Instagram only notifies you for accounts you follow. Peeqd monitors any public account and pings you the moment a story goes live.",
      defaultOpen: true,
    },
    {
      question: "Will they see me in their story viewers?",
      answer:
        "No. Peeqd alerts you to the event; you choose whether and how to view. We never view stories on your behalf.",
    },
    PEEQD_FAQ[2],
    PEEQD_FAQ[4],
    PEEQD_FAQ[5],
  ],
  "competitor-watch": [
    {
      question: "How many accounts can I watch?",
      answer:
        "Up to 10 on Pro and 40 on Agency. Each account gets full follow/unfollow tracking and story cadence in your daily digest.",
      defaultOpen: true,
    },
    {
      question: "Do I need to follow the accounts I watch?",
      answer:
        "No. Peeqd reads public data without following, liking, or interacting. Your watchlist is completely invisible to them.",
    },
    PEEQD_FAQ[2],
    PEEQD_FAQ[5],
  ],
  "audience-audit": [
    {
      question: "How do you detect fake followers?",
      answer:
        "We analyze follower patterns - account age, posting history, follower-to-following ratios, and engagement signals - to estimate what share of an audience is real vs. suspicious.",
      defaultOpen: true,
    },
    {
      question: "Is this useful before a brand deal?",
      answer:
        "Exactly what it's for. Know the real reach and engagement behind a vanity follower count before you spend on a partnership.",
    },
    PEEQD_FAQ[5],
    PEEQD_FAQ[6],
  ],
};

export function peeqdPageTopbar(crumb: string): PeeqdToolContent {
  return {
    ...PEEQD_TOOLS_CONTENT["followers-following"],
    crumb,
  };
}
